import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Empty,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { CrownOutlined, DeleteOutlined, EyeOutlined, SwapOutlined, UserAddOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Dictionary, DictionaryEditor, DictionaryViewer, DirectoryUser } from '@lens/shared';
import { ApiError } from '../../shared/api/client.ts';
import {
  addEditor as addEditorApi,
  addViewer as addViewerApi,
  deleteDictionary,
  listEditors,
  listUsers,
  listViewers,
  removeEditor as removeEditorApi,
  removeViewer as removeViewerApi,
  transferOwner as transferOwnerApi,
} from './api.ts';

const { Text, Paragraph } = Typography;

interface Props {
  dictionary: Dictionary;
  currentUserId: string | null;
  isPlatformAdmin: boolean;
  /** Вызывается после успешной передачи владения с обновлённым справочником. */
  onDictionaryChange: (next: Dictionary) => void;
}

/**
 * Вкладка «Права и доступ» справочника (REQUIREMENTS.md §2.4, FR-2.5).
 *
 * Секция «Владелец» — текущий владелец + кнопка передачи владения.
 * Секция «Редакторы» — список + добавление/удаление.
 *
 * Управлять доступом может только владелец или platform_admin
 * (`canManage`). Остальные видят состав read-only — серверные guard'ы
 * это дублируют (POST/DELETE editors, POST owner → 403).
 */
export function DictionaryAccessTab({
  dictionary,
  currentUserId,
  isPlatformAdmin,
  onDictionaryChange,
}: Props): JSX.Element {
  const canManage = isPlatformAdmin || dictionary.ownerId === currentUserId;

  const [editors, setEditors] = useState<DictionaryEditor[] | null>(null);
  const [editorsError, setEditorsError] = useState<string | null>(null);
  const [users, setUsers] = useState<DirectoryUser[]>([]);

  const [addUserId, setAddUserId] = useState<string | undefined>(undefined);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Наблюдатели — доступ «только просмотр» во вкладке «Справочники».
  const [viewers, setViewers] = useState<DictionaryViewer[] | null>(null);
  const [viewersError, setViewersError] = useState<string | null>(null);
  const [addViewerUserId, setAddViewerUserId] = useState<string | undefined>(undefined);
  const [addingViewer, setAddingViewer] = useState(false);
  const [removingViewerId, setRemovingViewerId] = useState<string | null>(null);

  const [transferUserId, setTransferUserId] = useState<string | undefined>(undefined);
  const [transferring, setTransferring] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const loadEditors = useCallback(async () => {
    setEditorsError(null);
    try {
      setEditors(await listEditors(dictionary.id));
    } catch (e) {
      setEditorsError(e instanceof ApiError ? e.message : 'Не удалось загрузить редакторов');
    }
  }, [dictionary.id]);

  useEffect(() => {
    void loadEditors();
  }, [loadEditors]);

  const loadViewers = useCallback(async () => {
    setViewersError(null);
    try {
      setViewers(await listViewers(dictionary.id));
    } catch (e) {
      setViewersError(e instanceof ApiError ? e.message : 'Не удалось загрузить наблюдателей');
    }
  }, [dictionary.id]);

  useEffect(() => {
    void loadViewers();
  }, [loadViewers]);

  // Список пользователей нужен только тем, кто управляет доступом.
  useEffect(() => {
    if (!canManage) return;
    let cancelled = false;
    void (async () => {
      try {
        const list = await listUsers();
        if (!cancelled) setUsers(list);
      } catch {
        // Пикер просто останется пустым — не критично для read-секции.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManage]);

  const editorIds = useMemo(() => new Set((editors ?? []).map((e) => e.userId)), [editors]);
  const viewerIds = useMemo(() => new Set((viewers ?? []).map((v) => v.userId)), [viewers]);

  // Кандидаты в редакторы: активные пользователи, кроме владельца и уже-редакторов.
  const editorCandidates = useMemo(
    () => users.filter((u) => u.id !== dictionary.ownerId && !editorIds.has(u.id)),
    [users, dictionary.ownerId, editorIds],
  );

  // Кандидаты в наблюдатели: кроме владельца, редакторов (они и так видят
  // справочник) и уже-наблюдателей.
  const viewerCandidates = useMemo(
    () =>
      users.filter(
        (u) => u.id !== dictionary.ownerId && !editorIds.has(u.id) && !viewerIds.has(u.id),
      ),
    [users, dictionary.ownerId, editorIds, viewerIds],
  );

  // Кандидаты на владение: любой пользователь, кроме текущего владельца.
  const ownerCandidates = useMemo(
    () => users.filter((u) => u.id !== dictionary.ownerId),
    [users, dictionary.ownerId],
  );

  const handleAddEditor = useCallback(async () => {
    if (!addUserId) return;
    setAdding(true);
    try {
      await addEditorApi(dictionary.id, addUserId);
      setAddUserId(undefined);
      await loadEditors();
      message.success('Редактор добавлен');
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : 'Не удалось добавить редактора');
    } finally {
      setAdding(false);
    }
  }, [addUserId, dictionary.id, loadEditors]);

  const handleRemoveEditor = useCallback(
    async (userId: string) => {
      setRemovingId(userId);
      try {
        await removeEditorApi(dictionary.id, userId);
        await loadEditors();
        message.success('Редактор удалён');
      } catch (e) {
        message.error(e instanceof ApiError ? e.message : 'Не удалось удалить редактора');
      } finally {
        setRemovingId(null);
      }
    },
    [dictionary.id, loadEditors],
  );

  const handleAddViewer = useCallback(async () => {
    if (!addViewerUserId) return;
    setAddingViewer(true);
    try {
      await addViewerApi(dictionary.id, addViewerUserId);
      setAddViewerUserId(undefined);
      await loadViewers();
      message.success('Наблюдатель добавлен');
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : 'Не удалось добавить наблюдателя');
    } finally {
      setAddingViewer(false);
    }
  }, [addViewerUserId, dictionary.id, loadViewers]);

  const handleRemoveViewer = useCallback(
    async (userId: string) => {
      setRemovingViewerId(userId);
      try {
        await removeViewerApi(dictionary.id, userId);
        await loadViewers();
        message.success('Наблюдатель удалён');
      } catch (e) {
        message.error(e instanceof ApiError ? e.message : 'Не удалось удалить наблюдателя');
      } finally {
        setRemovingViewerId(null);
      }
    },
    [dictionary.id, loadViewers],
  );

  const handleTransfer = useCallback(async () => {
    if (!transferUserId) return;
    setTransferring(true);
    try {
      const updated = await transferOwnerApi(dictionary.id, transferUserId);
      onDictionaryChange(updated);
      setTransferUserId(undefined);
      // Прежний владелец автоматически уходит в редакторы — перечитываем.
      await loadEditors();
      message.success('Владение передано');
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : 'Не удалось передать владение');
    } finally {
      setTransferring(false);
    }
  }, [transferUserId, dictionary.id, onDictionaryChange, loadEditors]);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    try {
      await deleteDictionary(dictionary.id);
      message.success('Справочник удалён');
      navigate('/dictionaries');
    } catch (e) {
      message.error(
        e instanceof ApiError
          ? e.message
          : 'Не удалось удалить справочник. Возможно, он используется в reference-полях.',
      );
      setDeleting(false);
    }
  }, [dictionary.id, navigate]);

  const editorColumns: ColumnsType<DictionaryEditor> = [
    {
      title: 'Пользователь',
      dataIndex: 'displayName',
      key: 'displayName',
      render: (_, ed) => (
        <Space size={6}>
          <Text>{ed.displayName}</Text>
          <Text type="secondary">@{ed.login}</Text>
        </Space>
      ),
    },
    ...(canManage
      ? [
          {
            title: '',
            key: 'actions',
            width: 120,
            align: 'right' as const,
            render: (_: unknown, ed: DictionaryEditor) => (
              <Popconfirm
                title="Убрать редактора?"
                okText="Убрать"
                cancelText="Отмена"
                onConfirm={() => handleRemoveEditor(ed.userId)}
              >
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  loading={removingId === ed.userId}
                  data-testid={`dict-editor-remove-${ed.userId}`}
                >
                  Убрать
                </Button>
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  const viewerColumns: ColumnsType<DictionaryViewer> = [
    {
      title: 'Пользователь',
      dataIndex: 'displayName',
      key: 'displayName',
      render: (_, v) => (
        <Space size={6}>
          <Text>{v.displayName}</Text>
          <Text type="secondary">@{v.login}</Text>
        </Space>
      ),
    },
    ...(canManage
      ? [
          {
            title: '',
            key: 'actions',
            width: 120,
            align: 'right' as const,
            render: (_: unknown, v: DictionaryViewer) => (
              <Popconfirm
                title="Убрать наблюдателя?"
                okText="Убрать"
                cancelText="Отмена"
                onConfirm={() => handleRemoveViewer(v.userId)}
              >
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  loading={removingViewerId === v.userId}
                  data-testid={`dict-viewer-remove-${v.userId}`}
                >
                  Убрать
                </Button>
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }} data-testid="dict-access-tab">
      {/* ─── Владелец ─────────────────────────────────────────────── */}
      <Card
        size="small"
        title={
          <Space>
            <CrownOutlined style={{ color: '#d4a017' }} />
            Владелец
          </Space>
        }
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space size={6} data-testid="dict-owner">
            <Text strong>{dictionary.owner.displayName}</Text>
            <Text type="secondary">@{dictionary.owner.login}</Text>
            {dictionary.ownerId === currentUserId && <Tag color="gold">это вы</Tag>}
          </Space>
          <Paragraph type="secondary" style={{ margin: 0 }}>
            Владелец настраивает колонки, назначает редакторов и наблюдателей, передаёт
            владение и удаляет справочник.
          </Paragraph>

          {canManage && (
            <Space.Compact style={{ width: '100%', maxWidth: 460 }}>
              <Select
                showSearch
                placeholder="Новый владелец…"
                style={{ flex: 1 }}
                value={transferUserId}
                onChange={setTransferUserId}
                optionFilterProp="label"
                data-testid="dict-owner-select"
                options={ownerCandidates.map((u) => ({
                  value: u.id,
                  label: `${u.displayName} (@${u.login})`,
                }))}
              />
              <Popconfirm
                title="Передать владение?"
                description="Вы станете редактором и потеряете права владельца."
                okText="Передать"
                cancelText="Отмена"
                disabled={!transferUserId}
                onConfirm={handleTransfer}
              >
                <Button
                  icon={<SwapOutlined />}
                  disabled={!transferUserId}
                  loading={transferring}
                  data-testid="dict-owner-transfer-btn"
                >
                  Передать
                </Button>
              </Popconfirm>
            </Space.Compact>
          )}
        </Space>
      </Card>

      {/* ─── Редакторы ────────────────────────────────────────────── */}
      <Card
        size="small"
        title={
          <Space>
            <UserAddOutlined />
            Редакторы
          </Space>
        }
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {canManage && (
            <Space.Compact style={{ width: '100%', maxWidth: 460 }}>
              <Select
                showSearch
                placeholder="Добавить редактора…"
                style={{ flex: 1 }}
                value={addUserId}
                onChange={setAddUserId}
                optionFilterProp="label"
                data-testid="dict-editor-add-select"
                options={editorCandidates.map((u) => ({
                  value: u.id,
                  label: `${u.displayName} (@${u.login})`,
                }))}
              />
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                disabled={!addUserId}
                loading={adding}
                onClick={handleAddEditor}
                data-testid="dict-editor-add-btn"
              >
                Добавить
              </Button>
            </Space.Compact>
          )}

          {editorsError ? (
            <Alert type="error" message={editorsError} showIcon />
          ) : editors === null ? (
            <Spin />
          ) : editors.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Редакторов пока нет — править записи может только владелец и platform-админ"
            />
          ) : (
            <Table<DictionaryEditor>
              rowKey="userId"
              size="small"
              pagination={false}
              columns={editorColumns}
              dataSource={editors}
              data-testid="dict-editors-table"
            />
          )}
        </Space>
      </Card>

      {/* ─── Наблюдатели ──────────────────────────────────────────── */}
      <Card
        size="small"
        title={
          <Space>
            <EyeOutlined />
            Наблюдатели
          </Space>
        }
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Paragraph type="secondary" style={{ margin: 0 }}>
            Наблюдатели видят справочник во вкладке «Справочники» в режиме
            только для чтения. По умолчанию справочник виден лишь создателю и
            администратору платформы; редакторы видят его автоматически.
            На списки в карточках и представлениях доступ не влияет.
          </Paragraph>

          {canManage && (
            <Space.Compact style={{ width: '100%', maxWidth: 460 }}>
              <Select
                showSearch
                placeholder="Добавить наблюдателя…"
                style={{ flex: 1 }}
                value={addViewerUserId}
                onChange={setAddViewerUserId}
                optionFilterProp="label"
                data-testid="dict-viewer-add-select"
                options={viewerCandidates.map((u) => ({
                  value: u.id,
                  label: `${u.displayName} (@${u.login})`,
                }))}
              />
              <Button
                type="primary"
                icon={<EyeOutlined />}
                disabled={!addViewerUserId}
                loading={addingViewer}
                onClick={handleAddViewer}
                data-testid="dict-viewer-add-btn"
              >
                Добавить
              </Button>
            </Space.Compact>
          )}

          {viewersError ? (
            <Alert type="error" message={viewersError} showIcon />
          ) : viewers === null ? (
            <Spin />
          ) : viewers.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Наблюдателей пока нет — справочник видят только создатель, владелец, редакторы и админ платформы"
            />
          ) : (
            <Table<DictionaryViewer>
              rowKey="userId"
              size="small"
              pagination={false}
              columns={viewerColumns}
              dataSource={viewers}
              data-testid="dict-viewers-table"
            />
          )}
        </Space>
      </Card>

      {/* ─── Удаление ─────────────────────────────────────────────── */}
      {canManage && (
        <Card
          size="small"
          title={
            <Space>
              <DeleteOutlined style={{ color: '#cf1322' }} />
              Удаление
            </Space>
          }
        >
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Paragraph type="secondary" style={{ margin: 0 }}>
              Удаление справочника необратимо. Доступно, только если справочник не
              используется reference-полями.
            </Paragraph>
            <Popconfirm
              title="Удалить справочник?"
              description="Это действие необратимо."
              okText="Удалить"
              okButtonProps={{ danger: true }}
              cancelText="Отмена"
              onConfirm={handleDelete}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={deleting}
                data-testid="dict-delete-btn"
              >
                Удалить справочник
              </Button>
            </Popconfirm>
          </Space>
        </Card>
      )}
    </Space>
  );
}
